
import React, { useState, useEffect } from 'react';
import { X, Shield, Globe, Lock, Search, Check } from 'lucide-react';

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');

const AssetAccessModal = ({ asset, users, onClose, onSave }) => {
    const [isPublic, setIsPublic] = useState(asset.is_public);
    const [shares, setShares] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Roles to show as shortcuts
    const roles = [
        { id: 'builder', label: 'All Builders', icon: <Shield size={14} /> },
        { id: 'admin', label: 'All Admins', icon: <Shield size={14} /> },
        { id: 'viewer', label: 'All Viewers', icon: <Shield size={14} /> },
        { id: 'all', label: 'Everyone (Global)', icon: <Globe size={14} /> }
    ];

    useEffect(() => {
        // Fetch current permissions for this asset (Skip if Bulk)
        if (asset.id === 'bulk') return;

        const fetchPermissions = async () => {
            try {
                const res = await fetch(`${API_BASE}/admin/asset/${asset.id}/permissions`, {
                    credentials: 'include',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                });
                const data = await res.json();
                if (res.ok) {
                    setIsPublic(data.is_public);
                    // Map shares to a simpler format for UI
                    const mappedShares = data.shares.map(s => ({
                        type: s.shared_with_role ? 'role' : 'user',
                        value: s.shared_with_role || s.shared_with,
                        id: s.id
                    }));
                    setShares(mappedShares);
                }
            } catch (err) {
                console.error("Error fetching permissions:", err);
            }
        };

        fetchPermissions();
    }, [asset.id]);

    const handleToggleRole = (roleId) => {
        const exists = shares.find(s => s.type === 'role' && s.value === roleId);
        if (exists) {
            setShares(shares.filter(s => !(s.type === 'role' && s.value === roleId)));
        } else {
            setShares([...shares, { type: 'role', value: roleId }]);
        }
    };

    const handleToggleUser = (userId) => {
        const exists = shares.find(s => s.type === 'user' && s.value === userId);
        if (exists) {
            setShares(shares.filter(s => !(s.type === 'user' && s.value === userId)));
        } else {
            setShares([...shares, { type: 'user', value: userId }]);
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        const success = await onSave(asset.id, isPublic, shares);
        setIsLoading(false);
        if (success) onClose();
    };

    const filteredUsers = users.filter(u =>
    (u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="modal-content" style={{ maxWidth: '500px', width: '95%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="icon-container" style={{ background: 'rgba(234, 179, 8, 0.1)', color: '#eab308' }}>
                        <Shield size={20} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Manage Access Control</h2>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)', margin: 0 }}>
                            {asset.asset_name} {asset.id !== 'bulk' ? `(${asset.asset_type})` : '(Bulk Action)'}
                        </p>
                    </div>
                </div>
                <button className="close-btn" onClick={onClose}><X size={20} /></button>
            </div>

            <div className="modal-body" style={{ padding: '24px' }}>
                {/* Global Visibility Toggle */}
                <div style={{
                    marginBottom: '24px', padding: '16px', borderRadius: '12px',
                    background: isPublic ? 'rgba(16, 185, 129, 0.05)' : 'rgba(255,255,255,0.02)',
                    border: '1px solid ' + (isPublic ? 'rgba(16, 185, 129, 0.2)' : 'var(--glass-border)'),
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {isPublic ? <Globe size={20} color="#10b981" /> : <Lock size={20} color="var(--text-dim)" />}
                        <div>
                            <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>Organization-Wide Public</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Anyone in {asset.org_id} can discover this</div>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsPublic(!isPublic)}
                        style={{
                            background: isPublic ? '#10b981' : 'rgba(255,255,255,0.1)',
                            border: 'none', width: '44px', height: '24px', borderRadius: '12px',
                            cursor: 'pointer', position: 'relative', transition: 'all 0.3s'
                        }}
                    >
                        <div style={{
                            position: 'absolute', top: '2px', left: isPublic ? '22px' : '2px',
                            width: '20px', height: '20px', background: 'white', borderRadius: '50%',
                            transition: 'all 0.3s'
                        }} />
                    </button>
                </div>

                {!isPublic && (
                    <>
                        {/* Role Based Access */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '12px' }}>
                                GRANT ACCESS BY ROLE
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                {roles.map(role => {
                                    const isSelected = shares.find(s => s.type === 'role' && s.value === role.id);
                                    return (
                                        <button
                                            key={role.id}
                                            onClick={() => handleToggleRole(role.id)}
                                            style={{
                                                padding: '8px 12px', borderRadius: '8px', border: '1px solid ' + (isSelected ? '#8b5cf6' : 'var(--glass-border)'),
                                                background: isSelected ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255,255,255,0.03)',
                                                color: isSelected ? '#a78bfa' : 'var(--text-dim)',
                                                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem',
                                                cursor: 'pointer', transition: 'all 0.2s'
                                            }}
                                        >
                                            {isSelected ? <Check size={14} /> : role.icon}
                                            {role.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Individual User Access */}
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '12px' }}>
                                GRANT ACCESS TO SPECIFIC USERS
                            </label>
                            <div style={{ position: 'relative', marginBottom: '12px' }}>
                                <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Search users by name or email..."
                                    style={{ paddingLeft: '34px', fontSize: '0.9rem', height: '38px', background: 'rgba(255,255,255,0.03)' }}
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div style={{
                                maxHeight: '180px', overflowY: 'auto', borderRadius: '8px', border: '1px solid var(--glass-border)',
                                background: 'rgba(255,255,255,0.01)'
                            }}>
                                {filteredUsers.map(u => {
                                    const isSelected = shares.find(s => s.type === 'user' && s.value === u.id);
                                    const isOwner = u.id === asset.owner_id;

                                    return (
                                        <div
                                            key={u.id}
                                            onClick={() => !isOwner && handleToggleUser(u.id)}
                                            style={{
                                                padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                borderBottom: '1px solid rgba(255,255,255,0.03)',
                                                cursor: isOwner ? 'default' : 'pointer',
                                                opacity: isOwner ? 0.6 : 1,
                                                background: isSelected ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '28px', height: '28px', borderRadius: '50%',
                                                    background: isOwner ? 'var(--text-dim)' : 'var(--accent-primary)',
                                                    display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: 'white'
                                                }}>
                                                    {u.name.charAt(0)}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '0.85rem', color: isSelected || isOwner ? 'var(--text-main)' : 'var(--text-dim)', fontWeight: 500 }}>
                                                        {u.name} {isOwner && <span style={{ fontSize: '0.7rem', color: '#eab308', marginLeft: '4px' }}>(Owner)</span>}
                                                        <span style={{ fontSize: '0.65rem', color: 'rgba(139, 92, 246, 0.7)', marginLeft: '6px', background: 'rgba(139, 92, 246, 0.08)', padding: '1px 6px', borderRadius: '4px' }}>{u.role.toUpperCase()}</span>
                                                    </span>
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', opacity: 0.7 }}>{u.email}</span>
                                                </div>
                                            </div>
                                            {isSelected && <Check size={16} color="#8b5cf6" />}
                                            {isOwner && <Lock size={14} color="var(--text-dim)" />}
                                        </div>
                                    );
                                })}
                                {filteredUsers.length === 0 && (
                                    <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '0.85rem' }}>
                                        No matching users found
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="modal-footer" style={{ borderTop: '1px solid var(--glass-border)', padding: '16px 24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={onClose} style={{ borderRadius: '8px' }}>Cancel</button>
                <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={isLoading}
                    style={{ borderRadius: '8px', padding: '8px 24px' }}
                >
                    {isLoading ? 'Saving...' : 'Apply Changes'}
                </button>
            </div>
        </div>
    );
};

export default AssetAccessModal;
